'use client';

import { useEffect, useRef, useState } from 'react';

interface AdPlaceholderProps {
  slot: string;
  format?: 'auto' | 'rectangle' | 'horizontal' | 'vertical';
  className?: string;
  style?: React.CSSProperties;
}

const AdPlaceholder: React.FC<AdPlaceholderProps> = ({
  slot,
  format = 'auto',
  className = '',
  style = {},
}) => {
  const adRef = useRef<HTMLElement>(null);
  const [isAdReady, setIsAdReady] = useState(false);
  const adsenseId = process.env.NEXT_PUBLIC_ADSENSE_ID;

  // Check if AdSense is configured (not placeholder value)
  const isConfigured = adsenseId && !adsenseId.includes('your-adsense-id');

  useEffect(() => {
    if (!isConfigured || !adRef.current) return;

    const timer = setTimeout(() => {
      if (window.adsbygoogle && adRef.current) {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          setIsAdReady(true);
        } catch (error) {
          console.error('AdSense error:', error);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isConfigured]);

  // Show placeholder when AdSense is not configured
  if (!isConfigured) {
    return (
      <div
        className={`bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center ${className}`}
        style={{ minHeight: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center', ...style }}
      >
        <div>
          <div className="text-gray-400 text-sm font-medium mb-2">📢 Ad Space Reserved</div>
          <div className="text-gray-500 text-xs">Slot: {slot}</div>
          <div className="text-gray-400 text-xs mt-1">Activate with custom domain</div>
        </div>
      </div>
    );
  }

  // Show actual AdSense ad when configured
  return (
    <div className={`w-full ${className}`} style={style}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', minHeight: '250px' }}
        data-ad-client={adsenseId}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
};

export default AdPlaceholder;